import { Carrier } from "../Carriers/Carrier";
import { parseShipmentBookingType } from "../Shipments";
import { getFirstActiveShipmentBox } from "./ShipmentBoxes";
import { ShipmentStatusEnum } from "./ShipmentStatus";
import { ShipmentDetailsApiRow } from "./ShipmentDetails";

export type BookingState =
  | "QUEUED"
  | "IN_PROGRESS"
  | "BOOKED"
  | "FAILED"
  | "CANCELLED";

export type BookingSyncIneligibilityReason =
  | "STATUS"
  | "TOO_OLD"
  | "BOOKING_STATE"
  | "BACKOFF"
  | "ATTEMPTS_EXCEEDED"
  | "CARRIER_NOT_API"
  | "MISSING_REQUIRED_FIELDS"
  | "INVALID_DECLARED_VALUE";

export type BookingSyncMissingField =
  | "weight"
  | "length"
  | "breadth"
  | "height"
  | "shipment_method"
  | "delivery_partner_id"
  | "declared_value"
  | "booking_type";

export type BulkBookingSyncResult = {
  scanned: number;
  queued: number;
  skipped: number;
  enqueueFailed: number;
  queuedShipmentIds: number[];
};

export type SingleBookingSyncResult = {
  shipmentId: number;
  booking_state: BookingState;
  carrier_ref_no: string;
  queued: boolean;
};

export type BookingSyncEligibility = {
  eligible: boolean;
  blockers: string[];
  missingFields: BookingSyncMissingField[];
};

const SINGLE_ATTEMPTS_CAP = 10;
const BOOKING_BACKOFF_MS = 5 * 60 * 1000;
const IN_PROGRESS_REQUEUE_MS = 60 * 60 * 1000;
const MAX_AGE_MONTHS = 6;
const BOOKABLE_STATES = new Set<string>(["FAILED", "CANCELLED"]);

const REASON_MESSAGES: Record<BookingSyncIneligibilityReason, string> = {
  STATUS: "Shipment must be Ready to Ship.",
  TOO_OLD: "Shipment is older than 6 months.",
  BOOKING_STATE: "Shipment is not in a bookable state.",
  BACKOFF: "Please wait 5 minutes before retrying booking.",
  ATTEMPTS_EXCEEDED: "Booking attempts exceeded.",
  CARRIER_NOT_API: "Delivery partner is not an API-integrated carrier.",
  MISSING_REQUIRED_FIELDS: "Required booking fields are missing.",
  INVALID_DECLARED_VALUE: "Declared value must be greater than 0.",
};

const BOOKING_TYPE_REQUIRED_MESSAGE = "Booking type is required.";

function getMissingFieldsBlockers(
  missingFields: BookingSyncMissingField[],
): string[] {
  const blockers: string[] = [];
  const otherMissing = missingFields.filter(
    (field) => field !== "declared_value" && field !== "booking_type",
  );

  if (missingFields.includes("booking_type")) {
    blockers.push(BOOKING_TYPE_REQUIRED_MESSAGE);
  }
  if (missingFields.includes("declared_value")) {
    blockers.push(REASON_MESSAGES.INVALID_DECLARED_VALUE);
  }
  if (otherMissing.length > 0) {
    blockers.push(`Missing required fields: ${otherMissing.join(", ")}.`);
  }

  return blockers;
}

function isPositiveNumber(value: unknown): boolean {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
}

function asDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isStaleInProgressBooking(
  details: Pick<
    ShipmentDetailsApiRow,
    "booking_state" | "last_booked_at"
  > | null | undefined,
): boolean {
  if (!details) {
    return false;
  }

  const bookingState = details.booking_state
    ? String(details.booking_state).toUpperCase()
    : "";
  if (bookingState !== "IN_PROGRESS") {
    return false;
  }

  const lastBookedAt = asDate(details.last_booked_at);
  if (!lastBookedAt) {
    return false;
  }

  return Date.now() - lastBookedAt.getTime() >= IN_PROGRESS_REQUEUE_MS;
}

export function findApiCarrierForShipment(
  deliveryPartnerId: number | string | null | undefined,
  carriers: Carrier[],
): Carrier | undefined {
  if (deliveryPartnerId == null || deliveryPartnerId === "") {
    return undefined;
  }

  const partnerId = Number(deliveryPartnerId);
  if (!Number.isFinite(partnerId)) {
    return undefined;
  }

  return carriers.find(
    (carrier) =>
      carrier.is_active &&
      String(carrier.integration_type || "").toUpperCase() === "API" &&
      (carrier.delivery_partner_id === partnerId || carrier.id === partnerId),
  );
}

export function getShipmentBookingEligibility(
  details: ShipmentDetailsApiRow | null | undefined,
  carriers: Carrier[],
): BookingSyncEligibility {
  const blockers: string[] = [];
  const missingFields: BookingSyncMissingField[] = [];

  if (!details) {
    return {
      eligible: false,
      blockers: ["Shipment details are not loaded."],
      missingFields,
    };
  }

  if (details.active === false || details.active === 0) {
    blockers.push("Shipment is inactive.");
  }

  if (details.status !== ShipmentStatusEnum.READY_TO_SHIP) {
    blockers.push(REASON_MESSAGES.STATUS);
  }

  const createdDate = asDate(details.created_date);
  if (createdDate) {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - MAX_AGE_MONTHS);
    if (createdDate < cutoff) {
      blockers.push(REASON_MESSAGES.TOO_OLD);
    }
  }

  const bookingState = details.booking_state
    ? String(details.booking_state).toUpperCase()
    : "";
  const staleInProgress = isStaleInProgressBooking(details);
  const isBookableState =
    !bookingState || BOOKABLE_STATES.has(bookingState) || staleInProgress;

  if (!isBookableState) {
    if (bookingState === "IN_PROGRESS") {
      blockers.push(
        "Booking is still in progress. You can retry after 1 hour.",
      );
    } else {
      blockers.push(REASON_MESSAGES.BOOKING_STATE);
    }
  }

  const lastBookedAt = asDate(details.last_booked_at);
  if (
    !staleInProgress &&
    lastBookedAt &&
    Date.now() - lastBookedAt.getTime() < BOOKING_BACKOFF_MS
  ) {
    blockers.push(REASON_MESSAGES.BACKOFF);
  }

  const attempts = Number(details.booking_attempts ?? 0);
  if (Number.isFinite(attempts) && attempts >= SINGLE_ATTEMPTS_CAP) {
    blockers.push(REASON_MESSAGES.ATTEMPTS_EXCEEDED);
  }

  const firstBox = getFirstActiveShipmentBox(details.boxes);
  if (!isPositiveNumber(firstBox?.weight)) missingFields.push("weight");
  if (!isPositiveNumber(firstBox?.length)) missingFields.push("length");
  if (!isPositiveNumber(firstBox?.breadth)) missingFields.push("breadth");
  if (!isPositiveNumber(firstBox?.height)) missingFields.push("height");
  if (!isPositiveNumber(details.declared_value)) {
    missingFields.push("declared_value");
  }
  if (!String(details.shipment_method || "").trim()) {
    missingFields.push("shipment_method");
  }
  if (
    details.delivery_partner_id == null ||
    Number(details.delivery_partner_id) <= 0
  ) {
    missingFields.push("delivery_partner_id");
  }
  if (!parseShipmentBookingType(details.booking_type)) {
    missingFields.push("booking_type");
  }

  if (missingFields.length > 0) {
    blockers.push(...getMissingFieldsBlockers(missingFields));
  } else if (
    !findApiCarrierForShipment(details.delivery_partner_id, carriers)
  ) {
    blockers.push(REASON_MESSAGES.CARRIER_NOT_API);
  }

  return {
    eligible: blockers.length === 0,
    blockers,
    missingFields,
  };
}

export function getBookingSyncErrorMessage(error: unknown): string {
  const data = (error as {
    response?: {
      data?: {
        message?: string;
        code?: string;
        details?: {
          reason?: BookingSyncIneligibilityReason;
          missing?: BookingSyncMissingField[];
        };
      };
    };
    message?: string;
  })?.response?.data;

  const reason = data?.details?.reason;
  const missing = data?.details?.missing;

  if (reason === "MISSING_REQUIRED_FIELDS" && missing?.length) {
    return getMissingFieldsBlockers(missing).join(" ");
  }

  if (reason && REASON_MESSAGES[reason]) {
    return REASON_MESSAGES[reason];
  }

  return (
    data?.message ||
    (error as { message?: string })?.message ||
    "Failed to queue shipment booking."
  );
}

export function getBulkBookingSyncMessage(result?: BulkBookingSyncResult): string {
  if (!result) {
    return "Eligible shipments queued for carrier booking.";
  }

  const parts = [
    `Queued ${result.queued} of ${result.scanned} scanned shipments`,
  ];
  if (result.skipped > 0) {
    parts.push(`${result.skipped} skipped`);
  }
  if (result.enqueueFailed > 0) {
    parts.push(`${result.enqueueFailed} failed to enqueue`);
  }
  return `${parts.join(", ")}.`;
}
